/**
 * §10.7 — Hazard definitions and the telegraph → arm → tick → expire lifecycle.
 * §8.8 — Hazard phase scheduling hooks (`startHazardEvent` / `stopHazardEvent`).
 *
 * Reserved presentation family (spine §12): magenta warning fill + striped edge.
 * Alpha ramp 0.15 → 0.50 then HOLD (no arm spike) — `hazardTelegraphAlpha`.
 * Enemy/boss attack fills use the separate 0 → 0.85 ramp — `enemyAttackAlpha`.
 *
 * Determinism: every position, offset and cycle orientation consumes `w.rng.spawn`
 * only (§8.12). No wall clock, no Math.random.
 *
 * The one exception is §10.7.4's storm, which consumes NO stream at all and hashes
 * `runSeed` with an ordinal instead — see `stormHash01` for the measurement that forced
 * it. It is still fully deterministic and still replays exactly; what it additionally
 * guarantees is that adding weather to the game moved no balance number.
 */

import type { World } from './world';
import { POOL_CAPACITY } from './world';
import type { Hazard, Vec2 } from './entities';
import { applyDamageToPlayer } from './damage';
import { applyStatus as applyStatusToTarget } from './status';
import { WRAP_HALF_H, WRAP_HALF_W, wrapDeltaX, wrapDeltaY, wrapX, wrapY } from './wrap';

// --- telegraph + presentation constants (§10.7, §11.9) ---

/** Spine §10 floor: nothing damaging arms with less than 1.5 s of warning. */
export const HAZARD_TELEGRAPH_MIN_TICKS = 90;
/** §8.8: a hazard *phase* announces itself 3 s before the first activation. */
export const HAZARD_PHASE_TELEGRAPH_TICKS = 180;
/** §11.9 hazard fill: ramp start alpha. */
export const HAZARD_ALPHA_START = 0.15;
/** §11.9 hazard fill: hold alpha reached at arm and held for the whole active window. */
export const HAZARD_ALPHA_HOLD = 0.5;
/** §11.9 enemy/boss attack fill peak, reached exactly at the arm tick. */
export const ENEMY_ATTACK_ALPHA_PEAK = 0.85;

/** §8.4 area-denial cap: total active zone area ≤ 12% of the arena. */
export const ZONE_AREA_FRACTION_CAP = 0.12;
/** §8.4 area-denial cap: ≤ 6 active zones. */
export const ZONE_COUNT_CAP = 6;

/** Damage cadence for every hazard that deals damage over time: 2 Hz (§10.7.1 "12 per 0.5 s"). */
export const HAZARD_DAMAGE_HZ = 2;
const HAZARD_DAMAGE_INTERVAL = 30;

// --- hazard IDs (§10.7, referenced by wave-phases.json and bosses.json) ---

export const HAZARD_VENT = 'hazard.hydrothermal-vent';
export const HAZARD_BRINE = 'hazard.brine-fall';
export const HAZARD_TIDE = 'hazard.tide-surge';
/** Boss-driven current lanes reuse the single current resolver (§10.7.3). */
export const HAZARD_BOSS_TIDE = 'hazard.abyssal-tide';
/** Warden mortar linger zones (bosses.json `mortar-barbs`) run on the hazard pipeline. */
export const HAZARD_MORTAR = 'hazard.mortar-pool';
/** Sovereign `last-light`: the whole arena except a marked safe disc. */
export const HAZARD_LAST_LIGHT = 'hazard.last-light';
/** §10.7.4 space-storm firebolt: a telegraphed strike disc on the ordinary hazard pipeline. */
export const HAZARD_FIREBOLT = 'hazard.storm-firebolt';

// --- §10.7.1 hydrothermal vent ---
const VENT_RADIUS = 3.5;
const VENT_EMITTERS = 5;
const VENT_DORMANT_MIN_TICKS = 360; // 6 s
const VENT_DORMANT_MAX_TICKS = 540; // 9 s
const VENT_TELEGRAPH_TICKS = 120; // 2.0 s
const VENT_ERUPT_TICKS = 180; // 3.0 s
const VENT_DPS = 24; // 12 per 0.5 s tick
const VENT_MIN_FROM_PLAYER = 10;
const VENT_MIN_SPACING = 6;
const VENT_MIN_FROM_BOSS = 10;

// --- §10.7.2 brine fall ---
const BRINE_RADIUS = 3.0;
const BRINE_FALL_TICKS = 90; // 1.5 s telegraph == fall time
const BRINE_POOL_TICKS = 480; // 8 s linger
const BRINE_CADENCE_TICKS = 72; // 1 glob per 1.2 s
const BRINE_MAX_POOLS = 8;
const BRINE_SPLASH = 10;
const BRINE_DPS = 6;
const BRINE_LEAD_S = 0.8;
const BRINE_LEAD_CHANCE = 0.7;
const BRINE_SCATTER_R = 25;
/** 5 stacks of status.slow (0.06 each) = ×0.70 move (§10.7.2 / status-effects.json). */
const BRINE_SLOW_STACKS = 5;
const BRINE_SLOW_MAGNITUDE = 0.06;
const BRINE_SLOW_DURATION_S = 2.0;

// --- §10.7.3 tide surge ---
const TIDE_LANES = 3;
const TIDE_WIDTH = 8;
const TIDE_LENGTH = 300; // spans the 200×200 arena at any orientation
const TIDE_TELEGRAPH_TICKS = 120; // 2.0 s
const TIDE_ACTIVE_TICKS = 360; // 6.0 s
const TIDE_REST_TICKS = 120; // 2.0 s
const TIDE_CYCLES = 6;
const TIDE_PUSH = 3.0;
const TIDE_MIN_LANE_SPACING = 12;

// --- §10.7.4 space storm ---
//
// The storm is GAMEPLAY, not a screen effect, and this is the file that says so: it
// damages (firebolts) and it removes information (the render veil and the radar
// interference both read `stormIntensity`). Every decision it makes is a pure function
// of `runSeed` and an ordinal, so a seed reproduces the same storms at the same ticks
// with the same strikes, and it survives a save/resume round-trip through `HazardState`.
//
// It deliberately does NOT go through `startHazardEvent`. That slot is the director's
// (`SCHEDULE` in `src/sim/director.ts` owns vent / brine / tide) and setting `event_id`
// would drag `hazardEventActive` with it, which §8.1 multiplies the spawn rate by. The
// storm is an overlay on whatever the director is already doing and moves no balance
// number: a storm during a vent phase is a storm during a vent phase.

/** Earliest a first storm can open: 90 s, past the §8 opening ramp. */
const STORM_FIRST_MIN_TICKS = 5400;
/** Gap between storms, sampled per storm. 105 s … 165 s — "from time to time". */
const STORM_PERIOD_MIN_TICKS = 6300;
const STORM_PERIOD_MAX_TICKS = 9900;
/** Envelope: 8 s roll-in, 15 s at full depth, 10 s clearing. 33 s of weather. */
const STORM_ONSET_TICKS = 480;
const STORM_PEAK_TICKS = 900;
const STORM_CLEAR_TICKS = 600;
const STORM_TOTAL_TICKS = STORM_ONSET_TICKS + STORM_PEAK_TICKS + STORM_CLEAR_TICKS;
/** Firebolt cadence at full intensity, and the slack either side of it. */
const STORM_BOLT_MIN_GAP = 132;
const STORM_BOLT_MAX_GAP = 252;
const STORM_BOLT_RADIUS = 2.6;
/**
 * 1.6 s of telegraph, comfortably over the §10 `HAZARD_TELEGRAPH_MIN_TICKS` floor of 90.
 * Stated explicitly rather than left to `spawnHazard`'s clamp so that the thunder cue,
 * which fires on the telegraph tick, has a documented lead over the strike.
 */
const STORM_BOLT_TELEGRAPH_TICKS = 96;
/** One 8-damage tick at the §10.7 2 Hz cadence: a bolt is a hit, not a pool. */
const STORM_BOLT_DPS = 16;
const STORM_BOLT_ACTIVE_TICKS = 12;
/**
 * A bolt never lands closer to the ship than this. The strike is aimed at where the
 * player IS, so with no standoff a stationary player would be hit by geometry rather
 * than by a decision, and the 1.6 s telegraph would be a countdown instead of a warning.
 * At 6 u against `player.move_speed` the dodge is always available and always cheap.
 */
const STORM_BOLT_STANDOFF = 6;
/** …and never further out than the frame, or the telegraph warns about nothing. */
const STORM_BOLT_REACH = 16;
/** Concurrent bolts. Three is legible; a fourth is a hazard field, not weather. */
const STORM_MAX_BOLTS = 3;
/**
 * The storm's randomness, and why it is a hash rather than a stream draw.
 *
 * Every other hazard in this file samples `w.rng.spawn`, which is correct for them: they
 * are director-scheduled events that already sit inside the spawn budget's causality. The
 * storm is not. It is an overlay that runs for the whole twenty minutes, and every value
 * it took from `rng.spawn` would re-phase every subsequent director decision in the run —
 * a weather effect silently re-rolling the spawn table, which is exactly the coupling the
 * six-stream split (§8.12) exists to prevent. The first draft did draw from the stream,
 * and the shipped seeds' spawn sequences moved; they do not move now.
 *
 * A hash of `runSeed` and an ordinal is just as reproducible, survives save/resume with
 * no extra state beyond the ordinals already in `HazardState`, and perturbs nothing. It
 * is the same argument `renderer.ts`'s `idHash01` makes for per-entity visual variance,
 * one layer down.
 */
function stormHash01(seed: number, salt: number, ordinal: number): number {
  let h = (Math.imul(seed ^ 0x9e3779b9, 2654435761) + Math.imul(salt, 40503) + Math.imul(ordinal + 1, 2246822519)) >>> 0;
  h ^= h >>> 15;
  h = Math.imul(h, 2246822519) >>> 0;
  h ^= h >>> 13;
  h = Math.imul(h, 3266489917) >>> 0;
  h ^= h >>> 16;
  return (h >>> 8) / 16777216;
}

const STORM_SALT_PERIOD = 0x5701;
const STORM_SALT_GAP = 0x5702;
const STORM_SALT_BEARING = 0x5703;
const STORM_SALT_RANGE = 0x5704;

/**
 * Hazard-pool slots the storm will not touch.
 *
 * `POOL_CAPACITY.hazards` is 32 and the pool is shared with the director's vents, the
 * boss patterns and R3's zones. `spawnHazard` emits `pool_exhausted` when it runs dry,
 * and `tools/bench.mjs` asserts that counter is exactly zero — so an unbudgeted storm
 * would fail the benchmark rather than merely look crowded. The storm yields instead.
 */
const STORM_POOL_RESERVE = 12;

/** §10.7.2 mortar pools (`bosses.json` warden phase 2): linger 2 s at 8 dmg/s. */
const MORTAR_RADIUS = 3.0;
const MORTAR_POOL_TICKS = 120;
const MORTAR_DPS = 8;

type ZoneShape = 'circle' | 'rect';

/**
 * The player-damage sink. Defaults to a self-contained intake that honours the dash
 * i-frames and the §3.7 mercy window; the integrator redirects it to R1's damage.ts.
 */
export type PlayerDamageFn = (w: World, amount: number, sourceDefId: string, sourceId: number) => void;
/** The status-application sink. Defaults to writing directly into the A.4 status slots. */
export type StatusApplyFn = (w: World, statusId: string, stacks: number, magnitude: number, durationS: number, sourceId: number) => void;

let damageSink: PlayerDamageFn = defaultPlayerDamage;
let statusSink: StatusApplyFn = defaultApplyStatus;

export function setPlayerDamageSink(fn: PlayerDamageFn): void {
  damageSink = fn;
}

export function setStatusSink(fn: StatusApplyFn): void {
  statusSink = fn;
}

/** Single funnel for every R4 player-damage path (hazard ticks and boss patterns). */
export function applyPlayerDamage(w: World, amount: number, sourceDefId: string, sourceId: number): void {
  damageSink(w, amount, sourceDefId, sourceId);
}

/** Single funnel for every R4 status application. */
export function applyStatus(w: World, statusId: string, stacks: number, magnitude: number, durationS: number, sourceId: number): void {
  statusSink(w, statusId, stacks, magnitude, durationS, sourceId);
}

/** §4.10 intake — dash i-frames, mercy window, armour, surge penalty, hit-stop (R1). */
function defaultPlayerDamage(w: World, amount: number, sourceDefId: string, sourceId: number): void {
  applyDamageToPlayer(w, amount, sourceId, sourceDefId);
}

/** §4.9 — player-side statuses from §10 hazards use the same framework, symmetric fields. */
function defaultApplyStatus(w: World, statusId: string, stacks: number, magnitude: number, durationS: number, sourceId: number): void {
  applyStatusToTarget(w, w.player, statusId, stacks, sourceId, magnitude);
}

// --- per-world runtime state (WeakMap keeps World untouched; lookup never affects sim order) ---

/** Serializable hazard-system state (A.6 save/resume). */
export interface HazardState {
  event_id: string;
  event_end_tick: number;
  event_next_tick: number;
  event_cycle: number;
  event_orientation: number;
  /** Restart tick per hazard pool slot (vent dormancy); -1 when the slot does not cycle. */
  restart_at: number[];
  next_damage: number[];
  splashed: number[];
  brine_order: number[];
  brine_serial: number;
  /**
   * Entity id the side-arrays above were initialised for. The hazard pool is shared with R3
   * (nettle zones, volatile rings), so a slot can arrive here without passing `spawnHazard`;
   * a stamp mismatch means "initialise this slot now".
   */
  stamp: number[];
  /** §10.7.4 — tick the running storm opened, or -1 when the sky is clear. */
  storm_start: number;
  /** Tick the next storm opens. 0 means "not scheduled yet"; see `advanceStorm`. */
  storm_next: number;
  /** Tick the next firebolt begins its telegraph. */
  storm_next_bolt: number;
  /** Last envelope edge announced on the bus: 0 none, 1 onset, 2 peak, 3 clearing. */
  storm_stage: number;
  /** Storms opened this run. Ordinal only, for the §13 event log and telemetry. */
  storm_count: number;
  /** Firebolts fired this run. The ordinal `stormHash01` places each strike from. */
  storm_bolt_seq: number;
}

interface HazardRuntime extends HazardState {
  scratch: Vec2;
}

const RUNTIME = new WeakMap<World, HazardRuntime>();

function runtime(w: World): HazardRuntime {
  let st = RUNTIME.get(w);
  if (st === undefined) {
    const n = POOL_CAPACITY.hazards;
    st = {
      event_id: '',
      event_end_tick: 0,
      event_next_tick: 0,
      event_cycle: 0,
      event_orientation: 0,
      restart_at: new Array<number>(n).fill(-1),
      next_damage: new Array<number>(n).fill(0),
      splashed: new Array<number>(n).fill(0),
      brine_order: new Array<number>(n).fill(0),
      brine_serial: 1,
      stamp: new Array<number>(n).fill(0),
      storm_start: -1,
      storm_next: 0,
      storm_next_bolt: 0,
      storm_stage: 0,
      storm_count: 0,
      storm_bolt_seq: 0,
      scratch: { x: 0, y: 0 },
    };
    RUNTIME.set(w, st);
  }
  return st;
}

// --- public geometry helpers (render, boss and director read these) ---

/**
 * Point-in-zone test in hazard-local space. `pad` inflates by an entity radius.
 * The offset is the shortest toroidal one, so a zone straddling the seam covers both sides.
 */
export function insideZone(h: Hazard, x: number, y: number, pad: number): boolean {
  const dx = wrapDeltaX(x - h.pos.x);
  const dy = wrapDeltaY(y - h.pos.y);
  if (h.zone.shape === 'circle') {
    const r = h.zone.r + pad;
    return dx * dx + dy * dy <= r * r;
  }
  const c = Math.cos(h.rot);
  const s = Math.sin(h.rot);
  const along = dx * c + dy * s;
  const across = -dx * s + dy * c;
  return Math.abs(along) <= h.zone.h * 0.5 + pad && Math.abs(across) <= h.zone.w * 0.5 + pad;
}

/** §11.9 hazard fill alpha: 0.15 → 0.50 across the telegraph, then HOLD. No arm spike. */
export function hazardTelegraphAlpha(h: Hazard, tick: number): number {
  const start = h.spawn_tick;
  const end = h.telegraph_until;
  if (tick >= end || end <= start) return HAZARD_ALPHA_HOLD;
  const t = (tick - start) / (end - start);
  return HAZARD_ALPHA_START + (HAZARD_ALPHA_HOLD - HAZARD_ALPHA_START) * (t < 0 ? 0 : t);
}

/** §11.9 enemy/boss attack fill alpha: 0 → 0.85, reaching 0.85 exactly at `armTick`. */
export function enemyAttackAlpha(startTick: number, armTick: number, tick: number): number {
  if (armTick <= startTick) return ENEMY_ATTACK_ALPHA_PEAK;
  if (tick >= armTick) return ENEMY_ATTACK_ALPHA_PEAK;
  const t = (tick - startTick) / (armTick - startTick);
  return ENEMY_ATTACK_ALPHA_PEAK * (t < 0 ? 0 : t);
}

/** True while the hazard is armed and dealing/pushing (between arm and end). */
export function isArmed(h: Hazard, tick: number): boolean {
  return tick >= h.arm_tick && tick < h.end_tick;
}

/** Total active zone area, for the §8.4 / G3 coverage cap. */
export function activeZoneArea(w: World): number {
  const list = w.hazards.liveIndices();
  let area = 0;
  for (let i = 0; i < list.length; i++) {
    const h = w.hazards.slots[list[i]];
    if (h.hazard_id === HAZARD_TIDE || h.hazard_id === HAZARD_BOSS_TIDE) continue;
    if (h.zone.shape === 'circle') area += Math.PI * h.zone.r * h.zone.r;
    else area += h.zone.w * h.zone.h;
  }
  return area;
}

/** Number of active damaging zones (currents excluded — they deal no damage). */
export function activeZoneCount(w: World): number {
  const list = w.hazards.liveIndices();
  let n = 0;
  for (let i = 0; i < list.length; i++) {
    const h = w.hazards.slots[list[i]];
    if (h.damage_per_s > 0) n++;
  }
  return n;
}

/**
 * §10.7.3 single current resolver. Accumulates the push (u/s) at a world point from
 * every armed current lane, hazard or boss. Writes into `out`; never allocates.
 */
export function hazardPush(w: World, x: number, y: number, out: Vec2): void {
  out.x = 0;
  out.y = 0;
  const tick = w.tick;
  const list = w.hazards.liveIndices();
  for (let i = 0; i < list.length; i++) {
    const h = w.hazards.slots[list[i]];
    if (h.hazard_id !== HAZARD_TIDE && h.hazard_id !== HAZARD_BOSS_TIDE) continue;
    if (!isArmed(h, tick)) continue;
    if (!insideZone(h, x, y, 0)) continue;
    out.x += Math.cos(h.rot) * h.damage_per_s;
    out.y += Math.sin(h.rot) * h.damage_per_s;
  }
}

/** True while any current lane is armed — used by the G1 lane-overlap pair check. */
export function currentsActive(w: World): boolean {
  const tick = w.tick;
  const list = w.hazards.liveIndices();
  for (let i = 0; i < list.length; i++) {
    const h = w.hazards.slots[list[i]];
    if (h.hazard_id !== HAZARD_TIDE && h.hazard_id !== HAZARD_BOSS_TIDE) continue;
    if (isArmed(h, tick)) return true;
  }
  return false;
}

// --- spawn ---

/**
 * Create one hazard instance. Telegraph is clamped up to the §10 1.5 s floor for any
 * hazard that deals damage; zero-damage currents keep their scripted preview length
 * (still ≥ 2.0 s per §10.7.3). Returns null when the hazard pool is exhausted.
 */
export function spawnHazard(
  w: World,
  hazardId: string,
  x: number,
  y: number,
  shape: ZoneShape,
  size: number,
  telegraphS: number,
  durationS: number,
): Hazard | null {
  const h = w.hazards.acquire();
  if (h === null) {
    w.bus.emit('pool_exhausted', { pool: 'hazards', denied_kind: hazardId });
    return null;
  }
  let telegraphTicks = Math.round(telegraphS * 60);
  if (telegraphTicks < HAZARD_TELEGRAPH_MIN_TICKS) telegraphTicks = HAZARD_TELEGRAPH_MIN_TICKS;
  const tick = w.tick;
  h.id = w.allocId();
  h.hazard_id = hazardId;
  // Single canonicalisation point for every hazard placement — callers pass raw world coords.
  h.pos.x = wrapX(x);
  h.pos.y = wrapY(y);
  h.vel.x = 0;
  h.vel.y = 0;
  h.hp = 1;
  h.max_hp = 1;
  h.faction = 'neutral';
  h.targetable = false;
  h.status_count = 0;
  h.spawn_tick = tick;
  h.zone.shape = shape;
  h.zone.r = shape === 'circle' ? size : 0;
  h.zone.w = shape === 'rect' ? size : 0;
  h.zone.h = shape === 'rect' ? TIDE_LENGTH : 0;
  h.hitbox_radius = shape === 'circle' ? size : size * 0.5;
  h.telegraph_until = tick + telegraphTicks;
  h.arm_tick = h.telegraph_until;
  h.end_tick = h.arm_tick + Math.round(durationS * 60);
  h.damage_per_s = 0;
  h.status_id = '';
  h.owner_id = 0;
  h.rot = 0;
  const st = runtime(w);
  st.restart_at[h.pool_index] = -1;
  st.next_damage[h.pool_index] = h.arm_tick;
  st.splashed[h.pool_index] = 0;
  st.brine_order[h.pool_index] = 0;
  st.stamp[h.pool_index] = h.id;
  w.bus.emit('hazard', { hazard_id: hazardId, kind: 'telegraph', pos: h.pos, radius: shape === 'circle' ? size : size * 0.5 });
  return h;
}

// --- event scheduling (§8.8) ---

/**
 * Begin a scheduled hazard phase. Emitters/lanes are seeded from `w.rng.spawn` so a
 * seed reproduces the whole event. The first activation is 3 s out (§8.8).
 */
export function startHazardEvent(w: World, hazardId: string, durationS: number): void {
  const st = runtime(w);
  st.event_id = hazardId;
  st.event_end_tick = w.tick + Math.round(durationS * 60);
  st.event_cycle = 0;
  st.event_next_tick = w.tick;
  st.event_orientation = 0;
  if (hazardId === HAZARD_VENT) seedVentEmitters(w, st, VENT_EMITTERS, 0);
  else if (hazardId === HAZARD_BRINE) st.event_next_tick = w.tick + HAZARD_PHASE_TELEGRAPH_TICKS - BRINE_FALL_TICKS;
  else if (hazardId === HAZARD_TIDE) st.event_next_tick = w.tick + HAZARD_PHASE_TELEGRAPH_TICKS - TIDE_TELEGRAPH_TICKS;
}

/** End the running scheduled event; armed instances are released immediately (§10.7.1). */
export function stopHazardEvent(w: World): void {
  const st = runtime(w);
  st.event_id = '';
  st.event_end_tick = w.tick;
  releaseOwned(w, 0);
}

/** True while a scheduled hazard phase is running (drives §8.1's ×0.85 modifier). */
export function hazardEventActive(w: World): boolean {
  const st = runtime(w);
  return st.event_id !== '' && w.tick < st.event_end_tick;
}

export function activeHazardEventId(w: World): string {
  const st = runtime(w);
  return w.tick < st.event_end_tick ? st.event_id : '';
}

function releaseOwned(w: World, ownerId: number): void {
  const list = w.hazards.liveIndices();
  const st = runtime(w);
  for (let i = list.length - 1; i >= 0; i--) {
    const h = w.hazards.slots[list[i]];
    if (h.owner_id !== ownerId) continue;
    w.bus.emit('hazard', { hazard_id: h.hazard_id, kind: 'end', pos: h.pos, radius: h.zone.r });
    st.restart_at[h.pool_index] = -1;
    w.hazards.release(h);
  }
}

/**
 * §10.6 — warden phase 2 activates 2 vent emitters. `ownerId` ties them to the boss so
 * `releaseHazardsOwnedBy` can clear them on kill.
 */
export function spawnBossVents(w: World, ownerId: number, axisAngle: number): void {
  const st = runtime(w);
  const c = Math.cos(axisAngle);
  const s = Math.sin(axisAngle);
  for (let i = 0; i < 2; i++) {
    const d = i === 0 ? 20 : -20;
    const x = c * d;
    const y = s * d;
    const h = spawnHazard(w, HAZARD_VENT, x, y, 'circle', VENT_RADIUS, VENT_TELEGRAPH_TICKS / 60, VENT_ERUPT_TICKS / 60);
    if (h === null) return;
    h.owner_id = ownerId;
    h.damage_per_s = VENT_DPS;
    st.restart_at[h.pool_index] = h.end_tick + ventDormancy(w);
  }
}

export function releaseHazardsOwnedBy(w: World, ownerId: number): void {
  releaseOwned(w, ownerId);
}

function ventDormancy(w: World): number {
  return VENT_DORMANT_MIN_TICKS + Math.floor(w.rng.spawn.next() * (VENT_DORMANT_MAX_TICKS - VENT_DORMANT_MIN_TICKS + 1));
}

/** §10.7.1 emitter seeding: ≥ 10 u from player, ≥ 6 u apart, never under a boss. */
function seedVentEmitters(w: World, st: HazardRuntime, count: number, ownerId: number): void {
  let placed = 0;
  let guard = 0;
  while (placed < count && guard < 256) {
    guard++;
    // No walls to stay clear of — emitters sample the whole fundamental domain.
    const x = wrapX((w.rng.spawn.next() * 2 - 1) * WRAP_HALF_W);
    const y = wrapY((w.rng.spawn.next() * 2 - 1) * WRAP_HALF_H);
    const dpx = wrapDeltaX(x - w.player.pos.x);
    const dpy = wrapDeltaY(y - w.player.pos.y);
    if (dpx * dpx + dpy * dpy < VENT_MIN_FROM_PLAYER * VENT_MIN_FROM_PLAYER) continue;
    if (tooCloseToExisting(w, x, y, VENT_MIN_SPACING)) continue;
    if (tooCloseToBoss(w, x, y, VENT_MIN_FROM_BOSS)) continue;
    const h = spawnHazard(w, HAZARD_VENT, x, y, 'circle', VENT_RADIUS, VENT_TELEGRAPH_TICKS / 60, VENT_ERUPT_TICKS / 60);
    if (h === null) return;
    h.owner_id = ownerId;
    h.damage_per_s = VENT_DPS;
    // Stagger the first eruption so emitters do not fire in lockstep (§10.7.1 seeded offset).
    const offset = Math.floor(w.rng.spawn.next() * VENT_DORMANT_MIN_TICKS);
    h.telegraph_until = w.tick + HAZARD_PHASE_TELEGRAPH_TICKS + offset;
    h.arm_tick = h.telegraph_until;
    h.end_tick = h.arm_tick + VENT_ERUPT_TICKS;
    st.next_damage[h.pool_index] = h.arm_tick;
    st.restart_at[h.pool_index] = h.end_tick + ventDormancy(w);
    placed++;
  }
}

function tooCloseToExisting(w: World, x: number, y: number, min: number): boolean {
  const list = w.hazards.liveIndices();
  for (let i = 0; i < list.length; i++) {
    const h = w.hazards.slots[list[i]];
    if (h.hazard_id !== HAZARD_VENT) continue;
    const dx = wrapDeltaX(h.pos.x - x);
    const dy = wrapDeltaY(h.pos.y - y);
    if (dx * dx + dy * dy < min * min) return true;
  }
  return false;
}

function tooCloseToBoss(w: World, x: number, y: number, min: number): boolean {
  const list = w.enemies.liveIndices();
  for (let i = 0; i < list.length; i++) {
    const e = w.enemies.slots[list[i]];
    if (!e.is_boss) continue;
    const dx = wrapDeltaX(e.pos.x - x);
    const dy = wrapDeltaY(e.pos.y - y);
    if (dx * dx + dy * dy < min * min) return true;
  }
  return false;
}

// --- brine fall ---

function spawnBrineGlob(w: World, st: HazardRuntime): void {
  evictOldestPool(w, st);
  const p = w.player;
  let x: number;
  let y: number;
  if (w.rng.spawn.next() < BRINE_LEAD_CHANCE) {
    x = p.pos.x + p.vel.x * BRINE_LEAD_S;
    y = p.pos.y + p.vel.y * BRINE_LEAD_S;
  } else {
    const a = w.rng.spawn.next() * Math.PI * 2;
    const d = Math.sqrt(w.rng.spawn.next()) * BRINE_SCATTER_R;
    x = p.pos.x + Math.cos(a) * d;
    y = p.pos.y + Math.sin(a) * d;
  }
  const h = spawnHazard(w, HAZARD_BRINE, x, y, 'circle', BRINE_RADIUS, BRINE_FALL_TICKS / 60, (BRINE_POOL_TICKS) / 60);
  if (h === null) return;
  h.damage_per_s = BRINE_DPS;
  h.status_id = 'status.slow';
  st.brine_order[h.pool_index] = st.brine_serial++;
}

function countBrinePools(w: World): number {
  const list = w.hazards.liveIndices();
  let n = 0;
  for (let i = 0; i < list.length; i++) if (w.hazards.slots[list[i]].hazard_id === HAZARD_BRINE) n++;
  return n;
}

/** §10.7.2 "max 8 pools alive (oldest evaporates instantly)". */
function evictOldestPool(w: World, st: HazardRuntime): void {
  while (countBrinePools(w) >= BRINE_MAX_POOLS) {
    const list = w.hazards.liveIndices();
    let oldest = -1;
    let oldestOrder = 0;
    for (let i = 0; i < list.length; i++) {
      const h = w.hazards.slots[list[i]];
      if (h.hazard_id !== HAZARD_BRINE) continue;
      const order = st.brine_order[h.pool_index];
      if (oldest < 0 || order < oldestOrder) {
        oldest = h.pool_index;
        oldestOrder = order;
      }
    }
    if (oldest < 0) return;
    const h = w.hazards.slots[oldest];
    w.bus.emit('hazard', { hazard_id: h.hazard_id, kind: 'end', pos: h.pos, radius: h.zone.r });
    w.hazards.release(h);
  }
}

// --- tide surge ---

function spawnTideCycle(w: World, st: HazardRuntime, hazardId: string, lanes: number, push: number, activeTicks: number, telegraphTicks: number, ownerId: number): void {
  // Orientation: horizontal / vertical / diagonal ±15° (§10.7.3), seeded per cycle.
  const pick = Math.floor(w.rng.spawn.next() * 4);
  const base = pick === 0 ? 0 : pick === 1 ? Math.PI * 0.5 : pick === 2 ? Math.PI * 0.25 : -Math.PI * 0.25;
  const jitter = (w.rng.spawn.next() * 2 - 1) * (Math.PI / 12);
  const angle = base + jitter;
  st.event_orientation = angle;
  const nx = -Math.sin(angle);
  const ny = Math.cos(angle);
  const span = w.arenaHalfW + w.arenaHalfH;
  // Lane centres are offsets along the lane normal, ≥ 12 u apart.
  const spacing = TIDE_MIN_LANE_SPACING + w.rng.spawn.next() * 6;
  const first = -((lanes - 1) * spacing) * 0.5 + (w.rng.spawn.next() * 2 - 1) * (span * 0.15);
  const dir = w.rng.spawn.next() < 0.5 ? 1 : -1;
  for (let i = 0; i < lanes; i++) {
    const off = first + i * spacing;
    const h = spawnHazard(w, hazardId, nx * off, ny * off, 'rect', TIDE_WIDTH, telegraphTicks / 60, activeTicks / 60);
    if (h === null) return;
    h.rot = dir > 0 ? angle : angle + Math.PI;
    // `damage_per_s` carries the push magnitude for current lanes (they never damage).
    h.damage_per_s = push;
    h.owner_id = ownerId;
    st.next_damage[h.pool_index] = 0x7fffffff;
  }
}

/** §8.7 / bosses.json — `abyssal-tide`: boss-owned current lanes, 3 u/s for 4 s. */
export function spawnBossTide(w: World, ownerId: number, lanes: number, durationS: number, telegraphS: number): void {
  if (currentsActive(w)) return; // G1: hazard lanes never overlap boss tide lanes in time
  const st = runtime(w);
  spawnTideCycle(w, st, HAZARD_BOSS_TIDE, lanes, TIDE_PUSH, Math.round(durationS * 60), Math.round(telegraphS * 60), ownerId);
}

/** bosses.json warden `mortar-barbs` — a lobbed landing disc that lingers at 8 dmg/s. */
export function spawnMortarPool(w: World, ownerId: number, x: number, y: number, telegraphS: number): Hazard | null {
  const h = spawnHazard(w, HAZARD_MORTAR, x, y, 'circle', MORTAR_RADIUS, telegraphS, MORTAR_POOL_TICKS / 60);
  if (h === null) return null;
  h.damage_per_s = MORTAR_DPS;
  h.owner_id = ownerId;
  return h;
}

/**
 * bosses.json sovereign `last-light` — arena-wide pulse with one safe disc. Modelled as a
 * circle hazard flagged by `status_id === 'inverted'`: damage applies OUTSIDE the disc.
 */
export function spawnLastLight(w: World, ownerId: number, x: number, y: number, radius: number, telegraphS: number, damage: number): Hazard | null {
  const h = spawnHazard(w, HAZARD_LAST_LIGHT, x, y, 'circle', radius, telegraphS, 1 / 60);
  if (h === null) return null;
  h.status_id = 'inverted';
  h.damage_per_s = damage * HAZARD_DAMAGE_HZ;
  h.owner_id = ownerId;
  return h;
}

// --- §10.7.4 space storm ---

/**
 * The storm envelope at the current tick, 0 … 1.
 *
 * This is the storm's entire public surface, and every consumer reads it rather than
 * reconstructing a phase: the render veil's depth, the radar's interference density and
 * the firebolt cadence are all one function of one number. There is no second timeline
 * anywhere that could disagree with this one — the bug that produced a due-east aim line
 * out of every newborn enemy was exactly a second reader of a dual-purpose field, and the
 * cheapest way not to repeat it is to publish one value and derive nothing twice.
 *
 * Read-only over `World`, safe from `src/render/**`.
 */
export function stormIntensity(w: World): number {
  const st = runtime(w);
  const start = st.storm_start;
  if (start < 0) return 0;
  const age = w.tick - start;
  if (age < 0 || age >= STORM_TOTAL_TICKS) return 0;
  if (age < STORM_ONSET_TICKS) {
    const t = age / STORM_ONSET_TICKS;
    return t * t * (3 - 2 * t);
  }
  if (age < STORM_ONSET_TICKS + STORM_PEAK_TICKS) return 1;
  const t = 1 - (age - STORM_ONSET_TICKS - STORM_PEAK_TICKS) / STORM_CLEAR_TICKS;
  return t * t * (3 - 2 * t);
}

/** True while a storm is anywhere in its envelope, including the roll-in and the tail. */
export function stormActive(w: World): boolean {
  const st = runtime(w);
  return st.storm_start >= 0 && w.tick - st.storm_start < STORM_TOTAL_TICKS;
}

/** Storms opened this run. The §13 event log numbers them; nothing in §8 reads it. */
export function stormCount(w: World): number {
  return runtime(w).storm_count;
}

function scheduleNextStorm(w: World, st: HazardRuntime, from: number): void {
  const span = STORM_PERIOD_MAX_TICKS - STORM_PERIOD_MIN_TICKS;
  const r = stormHash01(w.runSeed, STORM_SALT_PERIOD, st.storm_count);
  st.storm_next = from + STORM_PERIOD_MIN_TICKS + Math.floor(r * (span + 1));
}

/**
 * Live firebolts. Counted rather than tracked in a side array because the pool is shared
 * and a bolt can expire through the ordinary `expire` path without telling anyone.
 */
function countFirebolts(w: World): number {
  const list = w.hazards.liveIndices();
  let n = 0;
  for (let i = 0; i < list.length; i++) if (w.hazards.slots[list[i]].hazard_id === HAZARD_FIREBOLT) n++;
  return n;
}

/**
 * One firebolt: aimed at the ship, offset by a seeded bearing and a seeded distance
 * inside [`STORM_BOLT_STANDOFF`, `STORM_BOLT_REACH`].
 *
 * The distance is sampled on `sqrt` so the strikes are uniform over the ANNULUS rather
 * than over the radius — a linear sample piles two thirds of the bolts into the inner
 * third of the ring, which is the difference between weather around the player and a
 * mortar barrage on top of them.
 *
 * Both values are hashed from `runSeed` and the bolt's ordinal, so a declined bolt costs
 * nothing and no stream can fall out of alignment — see `stormHash01`.
 */
function spawnFirebolt(w: World, st: HazardRuntime): void {
  const seq = st.storm_bolt_seq++;
  const a = stormHash01(w.runSeed, STORM_SALT_BEARING, seq) * Math.PI * 2;
  const d =
    Math.sqrt(stormHash01(w.runSeed, STORM_SALT_RANGE, seq)) * (STORM_BOLT_REACH - STORM_BOLT_STANDOFF) +
    STORM_BOLT_STANDOFF;
  const p = w.player;
  const x = p.pos.x + Math.cos(a) * d;
  const y = p.pos.y + Math.sin(a) * d;
  const h = spawnHazard(
    w,
    HAZARD_FIREBOLT,
    x,
    y,
    'circle',
    STORM_BOLT_RADIUS,
    STORM_BOLT_TELEGRAPH_TICKS / 60,
    STORM_BOLT_ACTIVE_TICKS / 60,
  );
  if (h === null) return;
  h.damage_per_s = STORM_BOLT_DPS;
  // The thunder IS the telegraph's opening tick. Nothing here makes a noise about danger
  // that is not already aimed and already on the board — see the `storm` payload doc.
  w.bus.emit('storm', { kind: 'thunder', intensity: stormIntensity(w), pos: h.pos });
}

/**
 * Storm scheduling and the firebolt cadence.
 *
 * Lazily schedules the first storm rather than doing it in `hazardReset`, because
 * `hazardReset` is also reached from `directorRestore` and drawing from `rng.spawn`
 * there would consume a value the saved stream has already spent. Here the first draw
 * happens on a fixed tick of a fixed step, so it reproduces exactly.
 */
function advanceStorm(w: World, st: HazardRuntime, tick: number): void {
  if (st.storm_next === 0) scheduleNextStorm(w, st, STORM_FIRST_MIN_TICKS - STORM_PERIOD_MIN_TICKS);

  const age = st.storm_start >= 0 ? tick - st.storm_start : -1;

  if (age >= 0 && age < STORM_TOTAL_TICKS) {
    const intensity = stormIntensity(w);
    // Announce each envelope edge exactly once. `storm_stage` is the latch, so a resumed
    // run does not re-announce an edge it already crossed before the save.
    const stage = age < STORM_ONSET_TICKS ? 1 : age < STORM_ONSET_TICKS + STORM_PEAK_TICKS ? 2 : 3;
    if (stage !== st.storm_stage) {
      st.storm_stage = stage;
      w.bus.emit('storm', {
        kind: stage === 1 ? 'onset' : stage === 2 ? 'peak' : 'clearing',
        intensity,
        pos: w.player.pos,
      });
    }
    // Bolts run through the peak and the tail, never during the roll-in: the veil has to
    // arrive before the thing it is the weather for, or the first strike lands out of a
    // clear sky and the storm reads as a reskinned mortar phase.
    if (age >= STORM_ONSET_TICKS && tick >= st.storm_next_bolt) {
      const r = stormHash01(w.runSeed, STORM_SALT_GAP, st.storm_bolt_seq);
      const gap = STORM_BOLT_MIN_GAP + Math.floor(r * (STORM_BOLT_MAX_GAP - STORM_BOLT_MIN_GAP + 1));
      // Cadence tightens with the envelope, so the tail thins out on its own.
      st.storm_next_bolt = tick + Math.round(gap / Math.max(0.35, intensity));
      const headroom = POOL_CAPACITY.hazards - w.hazards.liveIndices().length;
      if (headroom > STORM_POOL_RESERVE && countFirebolts(w) < STORM_MAX_BOLTS) {
        spawnFirebolt(w, st);
      }
    }
    return;
  }

  if (st.storm_start >= 0) {
    // The envelope has run out. Bolts already on the board keep their own telegraphs and
    // expire normally — a storm that vanished mid-warning would retract a promise.
    st.storm_start = -1;
    st.storm_stage = 0;
    w.bus.emit('storm', { kind: 'end', intensity: 0, pos: w.player.pos });
    scheduleNextStorm(w, st, tick);
    return;
  }

  if (tick >= st.storm_next) {
    st.storm_start = tick;
    st.storm_stage = 0;
    st.storm_count++;
    st.storm_next_bolt = tick + STORM_ONSET_TICKS;
  }
}

// --- step ---

/** Step 6 — telegraph → arm → tick damage → expire, plus scheduled-event bookkeeping. */
export function stepHazards(w: World): void {
  if (w.outcome !== 'running') return;
  const st = runtime(w);
  const tick = w.tick;

  advanceEvent(w, st, tick);
  advanceStorm(w, st, tick);

  const list = w.hazards.liveIndices();
  for (let i = 0; i < list.length; i++) {
    const h = w.hazards.slots[list[i]];
    adopt(st, h);
    if (tick === h.arm_tick) {
      w.bus.emit('hazard', { hazard_id: h.hazard_id, kind: 'activate', pos: h.pos, radius: h.zone.r });
      if (h.hazard_id === HAZARD_BRINE) applyBrineSplash(w, st, h);
      // §10.7.4: the frame the bolt lands. Separate from `hazard activate` because the
      // presentation beat is a strike down the sky, not a zone opening, and because a
      // consumer that wants only the storm should not have to filter the hazard stream.
      if (h.hazard_id === HAZARD_FIREBOLT) {
        w.bus.emit('storm', { kind: 'strike', intensity: stormIntensity(w), pos: h.pos });
      }
    }
    if (tick >= h.arm_tick && tick < h.end_tick) applyHazardTick(w, st, h, tick);
    if (tick >= h.end_tick) expire(w, st, h, tick);
  }

  applyCurrents(w);
}

/** Initialise the side-arrays for a slot this module has not seen before (R3-owned zones). */
function adopt(st: HazardRuntime, h: Hazard): void {
  const i = h.pool_index;
  if (st.stamp[i] === h.id) return;
  st.stamp[i] = h.id;
  st.restart_at[i] = -1;
  st.next_damage[i] = h.arm_tick;
  st.splashed[i] = 0;
  st.brine_order[i] = 0;
}

function expire(w: World, st: HazardRuntime, h: Hazard, tick: number): void {
  const restart = h.hazard_id === HAZARD_VENT ? st.restart_at[h.pool_index] : -1;
  if (restart >= 0 && tick >= restart && (h.owner_id !== 0 || tick < st.event_end_tick)) {
    // §10.7.1 vent cycle: dormant → telegraph → erupt → repeat until the event ends.
    h.spawn_tick = tick;
    h.telegraph_until = tick + VENT_TELEGRAPH_TICKS;
    h.arm_tick = h.telegraph_until;
    h.end_tick = h.arm_tick + VENT_ERUPT_TICKS;
    st.next_damage[h.pool_index] = h.arm_tick;
    st.restart_at[h.pool_index] = h.end_tick + ventDormancy(w);
    w.bus.emit('hazard', { hazard_id: h.hazard_id, kind: 'telegraph', pos: h.pos, radius: h.zone.r });
    return;
  }
  if (restart >= 0 && tick < restart) return; // dormant, keep the emitter alive
  w.bus.emit('hazard', { hazard_id: h.hazard_id, kind: 'end', pos: h.pos, radius: h.zone.r });
  st.restart_at[h.pool_index] = -1;
  w.hazards.release(h);
}

function applyBrineSplash(w: World, st: HazardRuntime, h: Hazard): void {
  if (st.splashed[h.pool_index] !== 0) return;
  st.splashed[h.pool_index] = 1;
  if (insideZone(h, w.player.pos.x, w.player.pos.y, w.player.hitbox_radius)) {
    damageSink(w, BRINE_SPLASH, h.hazard_id, h.id);
  }
}

function applyHazardTick(w: World, st: HazardRuntime, h: Hazard, tick: number): void {
  if (h.damage_per_s <= 0) return;
  if (h.hazard_id === HAZARD_TIDE || h.hazard_id === HAZARD_BOSS_TIDE) return;
  if (tick < st.next_damage[h.pool_index]) return;
  st.next_damage[h.pool_index] = tick + HAZARD_DAMAGE_INTERVAL;
  const p = w.player;
  const inside = insideZone(h, p.pos.x, p.pos.y, p.hitbox_radius);
  const hits = h.status_id === 'inverted' ? !inside : inside;
  if (!hits) return;
  damageSink(w, h.damage_per_s / HAZARD_DAMAGE_HZ, h.hazard_id, h.id);
  if (h.status_id === 'status.slow') {
    statusSink(w, 'status.slow', BRINE_SLOW_STACKS, BRINE_SLOW_MAGNITUDE, BRINE_SLOW_DURATION_S, h.id);
  }
}

/** §10.7.3 push resolution — dash ignores currents (32 u/s vs 3 u/s). */
function applyCurrents(w: World): void {
  if (!currentsActive(w)) return;
  const st = runtime(w);
  const out = st.scratch;
  const p = w.player;
  if (!p.dash.active) {
    hazardPush(w, p.pos.x, p.pos.y, out);
    if (out.x !== 0 || out.y !== 0) {
      p.pos.x += out.x / 60;
      p.pos.y += out.y / 60;
      w.wrapToArena(p.pos, p.hitbox_radius);
    }
  }
  const list = w.enemies.liveIndices();
  for (let i = 0; i < list.length; i++) {
    const e = w.enemies.slots[list[i]];
    if (e.is_boss) continue;
    hazardPush(w, e.pos.x, e.pos.y, out);
    if (out.x === 0 && out.y === 0) continue;
    e.pos.x += out.x / 60;
    e.pos.y += out.y / 60;
    w.wrapToArena(e.pos, e.hitbox_radius);
  }
}

function advanceEvent(w: World, st: HazardRuntime, tick: number): void {
  if (st.event_id === '') return;
  if (tick >= st.event_end_tick) {
    if (st.event_id !== '') {
      st.event_id = '';
      releaseOwned(w, 0);
    }
    return;
  }
  if (st.event_id === HAZARD_BRINE) {
    if (tick >= st.event_next_tick && tick + BRINE_FALL_TICKS < st.event_end_tick) {
      st.event_next_tick = tick + BRINE_CADENCE_TICKS;
      spawnBrineGlob(w, st);
    }
    return;
  }
  if (st.event_id === HAZARD_TIDE) {
    if (tick >= st.event_next_tick && st.event_cycle < TIDE_CYCLES) {
      st.event_cycle++;
      st.event_next_tick = tick + TIDE_TELEGRAPH_TICKS + TIDE_ACTIVE_TICKS + TIDE_REST_TICKS;
      spawnTideCycle(w, st, HAZARD_TIDE, TIDE_LANES, TIDE_PUSH, TIDE_ACTIVE_TICKS, TIDE_TELEGRAPH_TICKS, 0);
    }
  }
}

// --- save / resume (A.6) ---

export function hazardSnapshot(w: World): HazardState {
  const st = runtime(w);
  return {
    event_id: st.event_id,
    event_end_tick: st.event_end_tick,
    event_next_tick: st.event_next_tick,
    event_cycle: st.event_cycle,
    event_orientation: st.event_orientation,
    restart_at: st.restart_at.slice(),
    next_damage: st.next_damage.slice(),
    splashed: st.splashed.slice(),
    brine_order: st.brine_order.slice(),
    brine_serial: st.brine_serial,
    stamp: st.stamp.slice(),
    storm_start: st.storm_start,
    storm_next: st.storm_next,
    storm_next_bolt: st.storm_next_bolt,
    storm_stage: st.storm_stage,
    storm_count: st.storm_count,
    storm_bolt_seq: st.storm_bolt_seq,
  };
}

export function hazardRestore(w: World, s: HazardState): void {
  const st = runtime(w);
  st.event_id = s.event_id;
  st.event_end_tick = s.event_end_tick;
  st.event_next_tick = s.event_next_tick;
  st.event_cycle = s.event_cycle;
  st.event_orientation = s.event_orientation;
  copyInto(st.restart_at, s.restart_at, -1);
  copyInto(st.next_damage, s.next_damage, 0);
  copyInto(st.splashed, s.splashed, 0);
  copyInto(st.brine_order, s.brine_order, 0);
  copyInto(st.stamp, s.stamp, 0);
  st.brine_serial = s.brine_serial;
  // `?? ` and not `||`: every one of these has a meaningful falsy value (`storm_stage` 0
  // is "nothing announced", `storm_next` 0 is "unscheduled"), so a truthiness test would
  // silently rewrite a valid resume. The defaults are for a save written before A.6 knew
  // the storm existed, which restores as a clear sky with the schedule re-drawn.
  st.storm_start = s.storm_start ?? -1;
  st.storm_next = s.storm_next ?? 0;
  st.storm_next_bolt = s.storm_next_bolt ?? 0;
  st.storm_stage = s.storm_stage ?? 0;
  st.storm_count = s.storm_count ?? 0;
  st.storm_bolt_seq = s.storm_bolt_seq ?? 0;
}

function copyInto(dst: number[], src: readonly number[], fill: number): void {
  for (let i = 0; i < dst.length; i++) dst[i] = i < src.length ? src[i] : fill;
}

/** Full reset — called by `directorRestore` and by run restart. */
export function hazardReset(w: World): void {
  const st = runtime(w);
  st.event_id = '';
  st.event_end_tick = 0;
  st.event_next_tick = 0;
  st.event_cycle = 0;
  st.event_orientation = 0;
  st.brine_serial = 1;
  st.storm_start = -1;
  // 0, not a sampled tick: `hazardReset` is reached from `directorRestore` as well as
  // from a run start, and drawing from `rng.spawn` here would spend a value the restored
  // stream has already spent. `advanceStorm` schedules on its first step instead.
  st.storm_next = 0;
  st.storm_next_bolt = 0;
  st.storm_stage = 0;
  st.storm_count = 0;
  st.storm_bolt_seq = 0;
  for (let i = 0; i < st.restart_at.length; i++) {
    st.restart_at[i] = -1;
    st.next_damage[i] = 0;
    st.splashed[i] = 0;
    st.brine_order[i] = 0;
    st.stamp[i] = 0;
  }
}
